import { DeviceTypes } from '@eppendorf/app-configs';
import { DeviceEventHit } from '@eppendorf/vnls-device-service-utils';
import type { Event } from '@eppendorf/vnls-notifications-utils';
import {
  ButtonBase,
  DateRangePicker,
  DotsLoader,
  Icon,
  IconSizeClasses,
  Input,
  ValidationMessage,
  NoResultsToShow,
  QuickSelectOption,
} from '@eppendorf/vnls-react-components';
import cn from 'classnames';
import { subDays, subMonths } from 'date-fns';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'react-use';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import { useDeviceType } from '$components/app-config/useDeviceType';
import { FeatureIsEnabled } from '$components/feature-toogle/feature-is-enabled';
import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { useGetDevice } from '$features/devices/devices.api';
import { EventRow } from '$features/monitoring/events/components/event-row';
import { useRequestMonitoringEvents } from '$features/monitoring/events/useRequestMonitoringEvents';
import { useDateRangeChange } from '$features/monitoring/history-charts-v2/useDateRangeChange';
import { CreateReportButton } from '$features/monitoring/report';
import { canCreateReport } from '$features/monitoring/report/utils/can-create-report';
import {
  useGetSubscription,
  getMaxAllowedDays,
} from '$features/subscription/subscription-api';

import styles from './monitoring-events.module.scss';

const MAX_SEARCH_TERM_LENGTH = 120;
const SEARCH_DEBOUNCE_DELAY = 500;
const NOTIFY_NEW_EVENTS_SCROLL_OFFSET = 150;

export function MonitoringEvents() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const to = searchParams.get('to');
  const from = searchParams.get('from');
  const message = searchParams.get('message') || '';
  const [searchInputValue, setSearchInputValue] = useState<string>(message);
  const {
    events,
    requestStatus,
    loadMore,
    deviceHasNewEvents,
    resetNewEventsTracker,
    updateFirstPage,
  } = useRequestMonitoringEvents();
  const eventsSearchInputRef = useRef<HTMLInputElement>(null);
  const virtualListRef = useRef<VirtuosoHandle>(null);
  const { onRangeChange } = useDateRangeChange();
  const [selectedQuickOption, setSelectedQuickOption] = useState<QuickSelectOption>();

  const handleDateRangeChange: typeof onRangeChange = (dateRange) => {
    onRangeChange(dateRange);
    setSelectedQuickOption(undefined);
  };
  const { data: subscription } = useGetSubscription();

  const [scrollParent, setScrollParent] = useState<HTMLElement | undefined>();
  const [scrollPosition, setScrollPosition] = useState(0);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      let el: HTMLElement | null = node.parentElement;
      while (el) {
        const { overflowY } = getComputedStyle(el);
        if (overflowY === 'auto' || overflowY === 'scroll') {
          setScrollParent(el);
          break;
        }
        el = el.parentElement;
      }
    }
  }, []);

  // Listen to scroll events on the custom scroll parent
  useEffect(() => {
    if (!scrollParent) return undefined;

    const handleScroll = () => {
      setScrollPosition(scrollParent.scrollTop);
    };

    scrollParent.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollParent.removeEventListener('scroll', handleScroll);
  }, [scrollParent]);

  const { data: deviceData } = useGetDevice({
    manufacturer: searchParams.get('manufacturer') ?? '',
    serialNumber: searchParams.get('serialNumber') ?? '',
  });
  const deviceType = useDeviceType(deviceData);
  const canCreate = canCreateReport(deviceType as DeviceTypes);

  useEffect(() => {
    if (scrollPosition < NOTIFY_NEW_EVENTS_SCROLL_OFFSET && deviceHasNewEvents) {
      updateFirstPage();
      resetNewEventsTracker();
    }
  }, [scrollPosition, deviceHasNewEvents, updateFirstPage, resetNewEventsTracker]);

  const [, cancelDebounce] = useDebounce(
    () => {
      // don't trigger search for query shorter than 2 characters
      if (searchInputValue && searchInputValue?.length < 2) {
        return;
      }

      setSearchParams((prev) => {
        if (!searchInputValue) {
          prev.delete('message');
        } else {
          prev.set('message', searchInputValue);
        }

        return prev;
      });
    },
    SEARCH_DEBOUNCE_DELAY,
    [setSearchParams, searchInputValue],
  );

  const updateFullTextSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.target.value);
    cancelDebounce();
  };

  const isMaxSearchCharsExceeded =
    (searchInputValue || message).length >= MAX_SEARCH_TERM_LENGTH;

  const resetSearch = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete('from');
      prev.delete('to');
      prev.delete('message');
      setSearchInputValue('');

      return prev;
    });
  }, []);

  const clearSearchInput = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete('message');
      setSearchInputValue('');
      eventsSearchInputRef.current?.focus();

      return prev;
    });
  }, [searchInputValue]);

  const goToNewEvents = useCallback(() => {
    if (!scrollParent) return;

    scrollParent.scrollTo({ top: 0, behavior: 'smooth' });
    resetNewEventsTracker();
  }, [scrollParent, deviceHasNewEvents]);

  const itemContent = useCallback(
    (deviceEvent: Event | DeviceEventHit) => (
      <EventRow key={deviceEvent.sourceEventId} deviceEvent={deviceEvent} />
    ),
    [],
  );

  const isFetching = requestStatus.isFetching && events.length === 0;

  const maxAllowedDays = getMaxAllowedDays(subscription);

  const disabledDates = {
    before: subDays(new Date(), maxAllowedDays),
    after: new Date(),
  };

  return (
    <div ref={containerRef}>
      <FeatureIsEnabled feature="ESEVNLS_4482_STABILITY_REPORT">
        {canCreate && (
          <div>
            <CreateReportButton
              serialNumber={searchParams.get('serialNumber') || ''}
              manufacturer={searchParams.get('manufacturer') || ''}
              initialDateRange={{
                from: from ? new Date(Number(from)) : undefined,
                to: to ? new Date(Number(to)) : undefined,
              }}
            />
          </div>
        )}
      </FeatureIsEnabled>
      <div className="m-top-m m-bottom-xxl flex flex__dir--column">
        <DateRangePicker
          dateRange={{
            from: from ? new Date(Number(from)) : undefined,
            to: to ? new Date(Number(to)) : undefined,
          }}
          onDateRangeChange={handleDateRangeChange}
          quickSelect={{
            enabled: true,
            selectedOption: selectedQuickOption,
            onOptionSelect: (option) => {
              setSelectedQuickOption(option);
            },
          }}
          autoFocus
          defaultMonth={subMonths(disabledDates.after, 1)}
          disabled={disabledDates}
          startMonth={disabledDates.before}
          endMonth={disabledDates.after}
          className="m-bottom-m"
        />
        <div className="m-bottom-m">
          <Input
            id="eventsFullTextSearch"
            ref={eventsSearchInputRef}
            placeholder={t('monitoringDetail.eventsDetail.eventSearchInputPlaceholder')}
            maxLength={MAX_SEARCH_TERM_LENGTH}
            value={searchInputValue}
            invalid={isMaxSearchCharsExceeded}
            onChange={updateFullTextSearch}
            leadingVisual={
              <Icon name="search" alt="search input" size={IconSizeClasses.XSmall} />
            }
            trailingAction={
              searchInputValue.length ? (
                <Input.Action
                  icon={
                    <Icon name="close" alt="clear input" size={IconSizeClasses.XSmall} />
                  }
                  title={t('shared.clear')}
                  onClick={clearSearchInput}
                />
              ) : undefined
            }
          />
          {isMaxSearchCharsExceeded && (
            <ValidationMessage severity="error">
              {t('validationMessages.searchMaxLength120')}
            </ValidationMessage>
          )}
        </div>
      </div>

      <small className="font-size-s m-bottom-m">
        {t('monitoringDetail.eventsDetail.timezoneHint')}
      </small>

      {isFetching && <DotsLoader className="m-top-10xl m-bottom-10xl" />}

      {!isFetching && (
        <>
          <div className={styles.deviceEventHeader}>
            <div>{t('monitoringDetail.eventsDetail.type')}</div>
            <div>{t('monitoringDetail.eventsDetail.time')}</div>
            <div>{t('monitoringDetail.eventsDetail.event')}</div>
          </div>
          <div>
            {events.length === 0 && !requestStatus.isError ? (
              <NoResultsToShow onResetSearch={resetSearch} />
            ) : (
              <>
                <Virtuoso
                  customScrollParent={scrollParent}
                  data={events}
                  endReached={loadMore}
                  overscan={200}
                  style={{ height: '100%' }}
                  // eslint-disable-next-line security/detect-object-injection -- it is ok to access the data by index
                  itemContent={(index) => itemContent(events[index])}
                  ref={virtualListRef}
                />
                {deviceHasNewEvents &&
                  scrollPosition > NOTIFY_NEW_EVENTS_SCROLL_OFFSET && (
                    <div className={cn('container', styles.overlayBottom5)}>
                      <div className="row">
                        <ButtonBase
                          type="button"
                          className="col-offset-4 btn-base btn btn--primary btn--info btn--small p-s"
                          onClick={goToNewEvents}
                        >
                          <div className="flex flex__dir--row">
                            <div className="m-left-m p-top-xs p-right-s">
                              {t('monitoringDetail.eventsDetail.newEvents')}
                            </div>
                            <div className="m-left-m">
                              <Icon
                                name="back-to-top"
                                className="bg-white"
                                size={IconSizeClasses.XSmall}
                              />
                            </div>
                          </div>
                        </ButtonBase>
                      </div>
                    </div>
                  )}
              </>
            )}
          </div>
        </>
      )}
      {requestStatus.isError && !requestStatus.isLoading && (
        <LoadingErrorHint className="box p-s m-top-m" />
      )}
    </div>
  );
}
